import moment from 'moment';
import { useCallback, useRef } from 'react';
import { AccountSocketData, Action, EventType, ObjectType } from 'api/pushes';
import {
  FetchableResourceType,
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useAccountWsUpdates } from 'hooks/use-account-ws-updates';
import { useUserWsUpdates } from 'hooks/use-user-ws-updates';
import { logger } from 'services';
import {
  asValidAllocationObject,
  newAllocationEvent,
} from '../../../utils/events-conversion/common';
import { useAllocationAndSummaryManager } from '../../resource-summary-manager';
import { AllocationSocketManagerProps } from '../../types';
import { useTotalAllocationInUIManager } from '../../ui-total-allocation-manager';

export const useAllocationCreateWSManager = (
  props: AllocationSocketManagerProps,
): void => {
  const {
    sentRequestIds,
    schedulerRef,
    groupBy,
    excludeNonWorkingDays,
    workingDaysConfig,
    resourceManagementConfig,
  } = props;
  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { updateTotalAllocation } = useTotalAllocationInUIManager(props);

  const handleCreate = useCallback(
    (response: AccountSocketData<string>) => {
      logger.log('allocation-create ~ socketData', response);

      if (sentRequestIds.includes(response.requestId)) {
        logger.log(
          'allocation-create ~ returning as this action is already handled',
        );

        return;
      }

      const data = JSON.parse(response.data);
      const allocation = asValidAllocationObject({
        ...data,
        projectId: data.project.projectId,
        ...(data.user != null
          ? {
              userId: data.user.userId,
              ...(data.referenceTeam
                ? {
                    roleId: data.role?.roleId,
                    referenceTeamId: data.referenceTeam?.teamId,
                  }
                : {}),
            }
          : {
              teamId: data.team.teamId,
              roleId: data.team?.roleId,
            }),
      });

      schedulerRef.current.instance.suspendRefresh();

      const event = newAllocationEvent(
        groupBy,
        allocation,
        {
          excludeNonWorkingDays,
          workingDaysConfig,
        },
        resourceManagementConfig?.softAllocations.enabled,
      );

      schedulerRef.current.instance.eventStore.add(event);

      const resourceIdentifier: ResourceIdentifier =
        allocation.type === ResourceType.User
          ? {
              type: ResourceType.User,
              userId: allocation.userId,
              roleId: allocation.roleId,
              referenceTeamId: allocation.referenceTeamId,
            }
          : {
              type: ResourceType.Team,
              teamId: allocation.teamId,
            };

      updateTotalAllocation(
        allocation.projectId,
        resourceIdentifier,
        event.totalMinutes,
      );

      updateAllocationAndSummary(
        allocation.type === ResourceType.User
          ? {
              [FetchableResourceType.UserList]: [allocation.userId],
            }
          : {
              [FetchableResourceType.RoleList]: [allocation.roleId],
            },
        {
          fromDate: moment(allocation.startDate),
          toDate: moment(allocation.endDate),
        },
      );

      schedulerRef.current.instance.resumeRefresh(true);
    },
    [
      excludeNonWorkingDays,
      groupBy,
      resourceManagementConfig?.softAllocations.enabled,
      schedulerRef,
      sentRequestIds,
      updateAllocationAndSummary,
      updateTotalAllocation,
      workingDaysConfig,
    ],
  );
  const isCollaborator = useIsCurrentUserCollaborator();
  const accountSocketHandler = useCallback(
    (params) => {
      if (!isCollaborator) {
        handleCreate(params);
      }
    },
    [handleCreate, isCollaborator],
  );
  const userSocketHandler = useCallback(
    (params) => {
      if (isCollaborator) {
        handleCreate(params);
      }
    },
    [handleCreate, isCollaborator],
  );

  const accountSocketHandlerRef = useRef<any>();

  accountSocketHandlerRef.current = accountSocketHandler;

  const accountSocketHandlerProxy = useCallback((params) => {
    accountSocketHandlerRef.current?.(params);
  }, []);
  const userSocketHandlerRef = useRef<any>();

  userSocketHandlerRef.current = userSocketHandler;

  const userSocketHandlerProxy = useCallback((params) => {
    userSocketHandlerRef.current?.(params);
  }, []);

  useAccountWsUpdates(
    accountSocketHandlerProxy,
    EventType(Action.Create, ObjectType.ResourceAllocation),
  );

  useUserWsUpdates(
    userSocketHandlerProxy,
    EventType(Action.Create, ObjectType.ResourceAllocation),
  );
};
